import { Button, Group, TextInput } from '@mantine/core';
import _debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';

import { useValidateTrafficSourceKey } from '../api/validateTrafficSourceKey';
import { Fields, useTrafficSourceForm } from '../hooks/useTrafficSourceForm';

import { useKeyValidator } from '@/hooks/useKeyValidator';
import transliteration from '@/utils/transliteration';

type TrafficSourceFormProps = {
  isLoading: boolean;
  onSubmit: (params: Fields) => void;
  initialValues: Fields;
};

export const TrafficSourceForm = ({
  isLoading,
  onSubmit,
  initialValues,
}: TrafficSourceFormProps) => {
  const form = useTrafficSourceForm({ initialValues });
  const { mutateAsync: validateTrafficSourceKey } = useValidateTrafficSourceKey();
  const { validate } = useKeyValidator(validateTrafficSourceKey);

  const [isTyping, setIsTyping] = useState(false);

  const debouncedValidateRef = useRef(
    _debounce(async (key: string) => {
      const error = await validate({ key });
      form.setFieldError('key', error);
      setIsTyping(false);
    }, 800)
  );

  useEffect(() => {
    const debounced = debouncedValidateRef.current;
    return () => {
      debounced.cancel();
    };
  }, []);

  const setKeyWithValidation = (key: string) => {
    form.setFieldValue('key', key);
    setIsTyping(true);
    debouncedValidateRef.current(key);
  };

  const handleNameChange = (name: string) => {
    form.setFieldValue('name', name);
    const generatedKey = transliteration.toKeyFormat(name);
    setKeyWithValidation(generatedKey);
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <TextInput
        label="Название"
        autoComplete="off"
        required
        withAsterisk
        placeholder="Имя"
        value={form.values.name}
        onChange={(event) => handleNameChange(event.currentTarget.value)}
        mb="xl"
        error={form.errors.name}
      />
      <TextInput
        label="Ключ"
        autoComplete="off"
        required
        withAsterisk
        placeholder="Ключ"
        value={form.values.key}
        onChange={(event) => setKeyWithValidation(event.currentTarget.value)}
        mb="xl"
        error={form.errors.key}
      />
      <TextInput
        label="Команда"
        autoComplete="off"
        required
        withAsterisk
        placeholder="Команда"
        value={form.values.command}
        onChange={(event) => form.setFieldValue('command', event.currentTarget.value)}
        mb="xl"
        error={form.errors.command}
      />

      <Group justify="right">
        <Button
          loading={isLoading}
          type="submit"
          disabled={isTyping || isLoading || Object.keys(form.errors).length > 0}
        >
          Сохранить
        </Button>
      </Group>
    </form>
  );
};
